import {
  GaugeIcon,
  DropletIcon,
  VibrateIcon,
  ScanSearchIcon,
  RouteIcon,
  RulerIcon,
  OctagonAlertIcon,
  TriangleAlertIcon,
  BrainIcon,
  TimerIcon,
  ShieldAlertIcon,
  BadgeCheckIcon,
  SirenIcon,
  ClipboardListIcon,
  CompassIcon,
  Grid3x3Icon,
  SearchIcon,
  FileTextIcon,
} from 'lucide-react';

export const CHECKOUT_URL = 'https://implantrisk-en.impultienda.ar/checkout/';

export const IMAGES = {
  product: 'https://cdn.magicpatterns.com/uploads/fcKxVEPm2ByH3cb6urmCSS/IMPLANTRISK-EN.png',
  author: 'https://cdn.magicpatterns.com/uploads/wk6XP1bGazwtG74G7WmRQv/MICHAEL_CARTER_-_IMPLANTES.png',
};

export const subtleSignals = [
  { icon: GaugeIcon, text: 'A change in resistance.' },
  { icon: DropletIcon, text: 'Unexpected bleeding.' },
  { icon: VibrateIcon, text: 'A loss of stability.' },
  { icon: ScanSearchIcon, text: "An anatomical finding that doesn't match your expectations." },
  { icon: RouteIcon, text: "A drill path that doesn't feel right." },
  { icon: RulerIcon, text: 'A measurement that suddenly changes the plan.' },
];

export const beforeItems = [
  "You notice something unexpected during surgery and aren't immediately sure how significant it is.",
  "You continue because you're hoping the situation will correct itself.",
  'You rely heavily on memory when assessing unexpected findings.',
  'You worry about making the wrong decision under pressure.',
  'You recognize complications only after they become obvious.',
  'Every difficult case feels different.',
];

export const afterItems = [
  'You know the warning signs you should actively watch for.',
  'You recognize potential risk earlier.',
  'You have structured checklists to help organize your assessment.',
  'You know which findings deserve a pause and reassessment.',
  'You have a reference system for reviewing difficult situations.',
  'You approach unexpected findings with more structure and less guesswork.',
];

export const painPoints = [
  {
    icon: OctagonAlertIcon,
    text: "You're in the middle of an implant procedure and suddenly something doesn't look or feel the way you expected.",
  },
  {
    icon: TriangleAlertIcon,
    text: "You know complications can begin with subtle warning signs, but you don't have a structured system for recognizing them.",
  },
  {
    icon: BrainIcon,
    text: "You have studied implant surgery, but under pressure it's difficult to remember every risk factor and warning signal.",
  },
  {
    icon: TimerIcon,
    text: "You spend too much time reviewing notes before difficult cases because you don't want to overlook something important.",
  },
  {
    icon: ShieldAlertIcon,
    text: 'You worry that continuing when something feels wrong could turn a manageable situation into a more serious complication.',
  },
  {
    icon: BadgeCheckIcon,
    text: 'You want more confidence when dealing with unexpected findings during implant procedures.',
  },
];

export const clarityQuestions = [
  'What am I seeing?',
  'Why might it matter?',
  'What should make me pause and reassess?',
  'What information do I need before continuing?',
  'When should I seek additional clinical support?',
];

export type Volume = {
  numeral: string;
  title: string;
  intro: string[];
  includes: string[];
  note?: string;
  core?: boolean;
  protocol?: { step: string; text: string }[];
};

export const volumes: Volume[] = [
  {
    numeral: 'I',
    title: 'The Implant Warning Signs You Should Never Ignore',
    intro: [
      'Learn how to recognize the early signals that may indicate increased risk during implant treatment.',
      "You'll learn how to organize warning signs into practical categories instead of trying to remember isolated complications.",
    ],
    includes: [
      'Surgical warning signals',
      'Anatomical warning signals',
      'Mechanical warning signals',
      'Biological warning signals',
      'Unexpected intraoperative findings',
      'Red-flag recognition framework',
    ],
  },
  {
    numeral: 'II',
    title: 'Before You Drill: The Pre-Surgical Risk Scan',
    intro: [
      'The safest time to recognize many problems is before surgery begins.',
      "I'll walk you through a structured pre-surgical review designed to help you identify potential risk factors before you start.",
    ],
    includes: [
      'Patient risk review',
      'Site assessment',
      'Anatomical considerations',
      'Bone-related considerations',
      'Treatment-planning warning signs',
      'Pre-surgical questions',
      'Risk-screening checklist',
    ],
  },
  {
    numeral: 'III',
    title: 'The Implant Surgery Risk Radar',
    core: true,
    intro: [
      'This is the core of the system.',
      "You'll learn how to organize what you're observing during surgery so you can recognize when something deserves closer attention.",
    ],
    includes: [
      'Changes in resistance',
      'Unexpected bleeding',
      'Unexpected anatomy',
      'Drilling-related warning signs',
      'Position and angulation concerns',
      'Stability-related signals',
      'Unexpected findings',
      'When to pause and reassess',
    ],
    note: "The goal isn't to make you memorize hundreds of complications. It's to train you to notice the signals that deserve your attention.",
  },
  {
    numeral: 'IV',
    title: 'The "Stop, Reassess, Decide" Protocol',
    intro: [
      "One of the most dangerous habits in surgery is feeling pressure to continue simply because you've already started.",
      "This volume gives you a structured framework for situations where reality doesn't match the original plan. You'll learn a simple sequence:",
    ],
    protocol: [
      { step: 'STOP', text: 'Recognize that something has changed.' },
      { step: 'REASSESS', text: 'Review the relevant clinical information before proceeding.' },
      {
        step: 'DECIDE',
        text: 'Determine the appropriate next step within your training, clinical judgment, and available support.',
      },
    ],
    includes: [
      'Pause criteria',
      'Reassessment checklists',
      'Decision trees',
      'Unexpected finding worksheets',
      'Escalation considerations',
      'Documentation prompts',
    ],
  },
  {
    numeral: 'V',
    title: 'The Implant Complication Prevention Playbook',
    intro: [
      'Prevention starts before the complication becomes obvious.',
      "This volume organizes common risk scenarios into practical prevention frameworks. You'll explore:",
    ],
    includes: [
      'Biological risk signals',
      'Mechanical risk signals',
      'Anatomical risk signals',
      'Planning-related problems',
      'Surgical warning patterns',
      'Common failure pathways',
      'Prevention checklists',
      'Postoperative warning considerations',
    ],
  },
  {
    numeral: 'VI',
    title: 'The Difficult Implant Case Decision Manual',
    intro: [
      'Not every case should be approached the same way.',
      "This volume helps you structure your thinking when a case doesn't fit the \"routine\" category. You'll learn how to organize:",
    ],
    includes: [
      'Difficult anatomy',
      'Increased-risk situations',
      'Unexpected intraoperative findings',
      'Cases requiring additional assessment',
      'Cases that may warrant referral',
      'Cases where the original plan needs reconsideration',
      'Questions to ask before proceeding',
    ],
    note: "The objective isn't to make every case feel easy. It's to help you recognize when a case isn't routine.",
  },
];

export const tools = [
  {
    icon: SirenIcon,
    title: '30+ Implant Warning-Sign Protocols',
    text: 'Practical reference protocols organized around the signals you should watch for before and during implant treatment.',
  },
  {
    icon: ClipboardListIcon,
    title: 'Professional Surgical Checklists',
    text: 'Pre-surgical and intraoperative checklists designed to help you systematically review critical considerations.',
  },
  {
    icon: CompassIcon,
    title: 'Implant Decision Maps',
    text: 'Visual decision frameworks that help you organize your thinking when an unexpected finding appears.',
  },
  {
    icon: Grid3x3Icon,
    title: 'Implant Risk Assessment Matrices',
    text: 'Simple frameworks for categorizing potential risk factors and identifying situations that deserve additional evaluation.',
  },
  {
    icon: SearchIcon,
    title: 'Red Flag Quick-Reference Sheets',
    text: 'Fast-reference pages you can review before difficult cases.',
  },
  {
    icon: FileTextIcon,
    title: 'Clinical Case Worksheets',
    text: 'Use them to analyze difficult cases and identify where risk signals appeared.',
  },
];

export const caseQuestions = [
  'What should I be watching for?',
  'What changed?',
  'Does this finding require reassessment?',
  'Should I continue, pause, modify the plan, or seek additional support?',
];

export type Bonus = {
  number: number;
  title: string;
  value: number;
  image: string;
  description: string;
  listIntro?: string;
  list?: string[];
  tagline?: string;
};

export const bonuses: Bonus[] = [
  {
    number: 1,
    title: '50 Implant Cases: "What Would You Do?"',
    value: 47,
    image: 'https://cdn.magicpatterns.com/uploads/gsb9v5UAEaVj5Fw7FJPAba/BONUS_1_IMPLANTRISK-EN.png',
    description:
      'Fifty educational case scenarios designed to train your ability to recognize risk signals before reviewing the suggested analysis.',
    listIntro: 'Each case asks you to identify:',
    list: [
      'What stands out?',
      'What could be a warning sign?',
      'What information is missing?',
      'What deserves reassessment?',
      'What would you consider before proceeding?',
    ],
    tagline: "Don't just read about complications. Train your eye to recognize the signals.",
  },
  {
    number: 2,
    title: 'The 10-Minute Implant Risk Scan™',
    value: 37,
    image: 'https://cdn.magicpatterns.com/uploads/4thUULeoSbQ6tKcfy8cgh5/BONUS_2_IMPLANTRISK-EN.png',
    description: 'A rapid pre-surgical framework designed to help you systematically review a case before treatment.',
    listIntro: "Instead of reviewing everything randomly, you'll move through a structured sequence covering:",
    list: ['Patient', 'Site', 'Anatomy', 'Imaging', 'Risk factors', 'Surgical considerations', 'Final risk review'],
    tagline: 'One scan. One structured process. Fewer forgotten considerations.',
  },
  {
    number: 3,
    title: 'The Implant Complication Warning Library',
    value: 47,
    image: 'https://cdn.magicpatterns.com/uploads/oT93U6qqViKTCE6BLmATHo/BONUS_3_IMPLANTRISK-EN.png',
    description: 'A visual reference library organized around common implant-related risk scenarios.',
    listIntro: "For each scenario, you'll find:",
    list: ['Potential signal', 'Why it matters', 'What to reassess', 'Questions to consider'],
    tagline: 'This is designed to help you recognize patterns instead of memorizing isolated facts.',
  },
  {
    number: 4,
    title: 'The Difficult Case Decision Cards™',
    value: 37,
    image: 'https://cdn.magicpatterns.com/uploads/rpnxr2XVCkD5NmGgEx1yQv/BONUS_4_IMPLANTRISK-EN.png',
    description:
      "A collection of quick-reference clinical decision cards for challenging implant situations. Each card gives you a simple framework for organizing your thinking when the case doesn't go exactly as planned.",
    tagline: 'Keep them on your computer, tablet, or print them for your clinical reference materials.',
  },
  {
    number: 5,
    title: 'The 30-Point Implant Surgery Safety Checklist',
    value: 29,
    image: 'https://cdn.magicpatterns.com/uploads/bLj5BbyM5rm9UgR5ajmhRs/BONUS_5_IMPLANTRISK-EN.png',
    description: 'A comprehensive checklist covering the key considerations you should review around implant surgery.',
    tagline: 'Use it before difficult cases to create a more consistent review process.',
  },
];

export const offerIncludes = [
  'Lifetime access',
  'Immediate digital access',
  'No monthly fees',
  '6 premium volumes',
  '5 professional bonuses',
  '30+ warning-sign protocols',
  'Clinical checklists',
  'Decision maps',
  'Risk assessment tools',
];

export const guaranteeSteps = [
  'Get access.',
  'Open the six volumes.',
  'Review the warning-sign protocols.',
  'Use the checklists.',
  'Go through the case scenarios.',
  'Look at the decision maps.',
];

export const faqs = [
  {
    q: 'How will I receive Implant Risk Radar™?',
    a: "Immediately after your payment, you'll receive an email with access instructions for the complete digital library. You'll be able to access the six volumes and five bonuses digitally.",
  },
  {
    q: 'Is this a course?',
    a: 'No. Implant Risk Radar™ is designed as a practical clinical reference system, not a long video course. You can read it sequentially or jump directly to the section you need.',
  },
  {
    q: 'Is this only for implant specialists?',
    a: 'The material is designed for dental professionals involved in implant treatment who want a more structured way to review risks and warning signals. Your level of training and scope of practice should always determine which procedures you perform.',
  },
  {
    q: 'Does this teach me how to perform implant surgery?',
    a: 'Implant Risk Radar™ is not a substitute for hands-on surgical training, supervision, or specialist education. It focuses on recognizing potential warning signs, organizing risk assessment, and structuring clinical reassessment. It is designed to complement—not replace—appropriate education and clinical judgment.',
  },
  {
    q: 'Can I use the material before difficult cases?',
    a: 'Yes. That is one of the main purposes of the system. The checklists, risk scans, decision maps, and quick-reference tools are designed to make the material easier to review before challenging cases.',
  },
  {
    q: 'Are the files downloadable?',
    a: 'Yes. The materials are provided digitally and can be saved for personal reference.',
  },
  {
    q: 'Is this a one-time payment?',
    a: 'Yes. You pay $27 USD once. There are no monthly subscriptions or recurring charges.',
  },
  {
    q: 'How long do I have access?',
    a: 'Lifetime access to the materials included in your purchase.',
  },
  {
    q: "What happens if I don't like it?",
    a: "You have a 7-day guarantee. Review the material, explore the protocols and decide whether it's right for you. If you don't believe it provides value, contact us within the first 7 days for a full refund.",
  },
];

export const recentBuyers = [
  { name: 'Dr. Andrés Morales', country: 'Colombia' },
  { name: 'Dra. Valentina Rojas', country: 'Chile' },
  { name: 'Dr. Javier Hernández', country: 'Mexico' },
  { name: 'Dra. Lucía Fernández', country: 'Spain' },
  { name: 'Dr. Martín Acosta', country: 'Argentina' },
  { name: 'Dra. Camila Vargas', country: 'Peru' },
  { name: 'Dr. Diego Castillo', country: 'Ecuador' },
  { name: 'Dra. Sofía Méndez', country: 'Uruguay' },
  { name: 'Dr. Sebastián Ortiz', country: 'Costa Rica' },
  { name: 'Dra. Gabriela Núñez', country: 'Dominican Republic' },
  { name: 'Dr. Alejandro Ruiz', country: 'Guatemala' },
  { name: 'Dra. Isabel Torres', country: 'Panama' },
  { name: 'Dr. Ricardo Paredes', country: 'Bolivia' },
  { name: 'Dra. Mariana Silva', country: 'Paraguay' },
  { name: 'Dr. Fernando Rivera', country: 'Puerto Rico' },
  { name: 'Dra. Daniela Pérez', country: 'Venezuela' },
];
